import React from 'react';
import PropTypes from 'prop-types';
import { motion, useMotionTemplate } from 'framer-motion';
import { LuArrowUpRight } from 'react-icons/lu';
import GlassIcon from '../../../components/ui/GlassIcon';
import { GLASS_CHIP } from '../../../components/ui/glass';
import { fadeUp } from '../../../components/ui/motionPresets';
import useSpotlight from '../../../hooks/useSpotlight';
import { useTranslation } from '../../../i18n/LanguageContext';

/** Shortcut tile on the dashboard; a soft accent light follows the pointer. */
const QuickLinkCard = ({ title, description, icon: Icon, gradient, spotlightColor, onClick }) => {
  const { t } = useTranslation();
  const { pointerX, pointerY, onPointerMove } = useSpotlight();
  const spotlight = useMotionTemplate`radial-gradient(320px circle at ${pointerX}px ${pointerY}px, ${spotlightColor}, transparent 70%)`;

  return (
    <motion.div variants={fadeUp} onPointerMove={onPointerMove} className="tw-group tw-h-full">
      <button
        type="button"
        onClick={onClick}
        className="tw-relative tw-isolate tw-flex tw-h-full tw-w-full tw-flex-col tw-items-start tw-overflow-hidden tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/75 tw-p-5 tw-text-left tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_14px_36px_-28px_rgba(15,23,42,0.4)] tw-transition-[border-color,box-shadow] tw-duration-300 hover:tw-border-white hover:tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_22px_44px_-24px_rgba(15,23,42,0.35)] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/60 sm:tw-p-6 dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_14px_36px_-28px_rgba(0,0,0,0.9)] dark:hover:tw-border-white/20"
      >
        <motion.span
          aria-hidden="true"
          style={{ background: spotlight }}
          className="tw-pointer-events-none tw-absolute tw-inset-0 -tw-z-10 tw-opacity-0 tw-transition-opacity tw-duration-300 group-hover:tw-opacity-100"
        />

        <GlassIcon gradient={gradient}>
          <Icon aria-hidden="true" className="tw-h-6 tw-w-6" />
        </GlassIcon>
        <span className="tw-mt-4 tw-block tw-text-base tw-font-semibold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{title}</span>
        <span className="tw-mt-1 tw-block tw-text-sm tw-leading-relaxed tw-text-slate-600 dark:tw-text-slate-400">{description}</span>
        <span className="tw-mt-auto tw-block tw-pt-5">
          <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-px-3.5 tw-py-1.5 tw-text-sm tw-font-semibold tw-text-slate-700 tw-transition-transform tw-duration-200 group-active:tw-scale-95 dark:tw-text-slate-200 ${GLASS_CHIP}`}>
            {t('common.view')}
            <LuArrowUpRight
              aria-hidden="true"
              className="tw-h-4 tw-w-4 tw-transition-transform tw-duration-300 group-hover:-tw-translate-y-0.5 group-hover:tw-translate-x-0.5"
            />
          </span>
        </span>
      </button>
    </motion.div>
  );
};

QuickLinkCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  gradient: PropTypes.string.isRequired,
  spotlightColor: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

export default QuickLinkCard;
